import React from "react";
import { PawPrint } from "lucide-react";
import CommonButton from "../CommonButton";
import { Pricing } from "../../types/Response/CustomerResponse";


interface PackageCardProps {
    packageId?: number;
    packageTitle: string;
    packageSubtitle?: string;
    description: string;
    includes?: string[];
    pricing: Pricing;
    handleSelectPackage?: (item: Pricing) => void;
    hideButton?: boolean;
    className?: string;
    disableHover?: boolean;
    isPopular?: boolean;
    isSpecial?: boolean;
    validityMonths?: number;
    badgeText?: string;
    
includedBookings?: number
}
const PackageCard: React.FC<PackageCardProps> = ({
  packageTitle,
    packageSubtitle,
    description,
    includes,
    pricing,
    handleSelectPackage,
    hideButton,
    className = "",
    disableHover = false,
    isPopular: propIsPopular,
    isSpecial: propIsSpecial,
    validityMonths,
    badgeText,
    includedBookings
}) => {
    const subtitleLower = packageSubtitle?.toLowerCase() || "";
    const titleLower = packageTitle?.toLowerCase() || "";
    const isPopular =
        propIsPopular ??
        (subtitleLower.includes("popular") ||
            titleLower.includes("package 2") ||
            titleLower.includes("most popular"));

    const isSpecial =
        propIsSpecial ??
        (titleLower.includes("special") ||
            subtitleLower.includes("special") ||
            titleLower.includes("monthly") ||
            !!validityMonths);

    let cardStyles = "bg-white border-[#ECE7DF] text-gray-800";
    let titleStyles = "text-mainBrandColor";
    let subtitleStyles = "text-logoHeading";
    let descStyles = "text-iconColor";
    let featureChipStyles = "bg-gray-50/60 border-gray-100/80 hover:bg-gray-100/90";
    let featureTextStyles = "text-gray-700";
    let bulletStyles = "bg-mainBrandColor";
    let priceStyles = "text-mainBrandColor";
    let buttonVariantClass = "";

    if (isPopular) {
        cardStyles = "bg-gradient-to-b from-emerald-600 to-emerald-300 border-transparent shadow-xl text-white";
        titleStyles = "text-amber-300 font-bold tracking-widest";
        subtitleStyles = "text-white";
        descStyles = "text-emerald-100";
        featureChipStyles = "bg-white/10 border-white/10 hover:bg-white/20";
        featureTextStyles = "text-white";
        bulletStyles = "bg-amber-300";
        priceStyles = "text-amber-300";
        buttonVariantClass = "bg-amber-400 text-slate-900";
    } else if (isSpecial) {
        cardStyles = "bg-[#FAF8F5] border-[#DFD8CD] hover:shadow-xl text-gray-800";
        titleStyles = "text-indigo-600";
        bulletStyles = "bg-indigo-600";
    }

    const hoverClass = disableHover
        ? ""
        : "hover:-translate-y-1 hover:shadow-2xl";

    return (
        <div
            className={`
                rounded-[2rem]
                border
                transition-all
                duration-500
                overflow-hidden
                relative
                group
                flex
                h-full
                flex-col
                w-full
                ${cardStyles}
                ${className}
                ${hoverClass}
                group-hover:-translate-y-1
            `}
        >
            {!isPopular && !isSpecial && (
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-mainBrandColor" />
            )}

            {isPopular && (
                <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-400 to-amber-500 text-black text-[8px] md:text-[11px] uppercase tracking-wider font-extrabold px-3 py-1.5 rounded-full z-20 shadow-md">
                    🔥 Most Popular
                </div>
            )}

            {isSpecial && (
                <div className="absolute top-0 right-1 lg:top-4 lg:right-4 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[8px] md:text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full z-20">
                    ✨ Exclusive Offer
                </div>
            )}

            <div className={`absolute bottom-4 right-4 transition-transform duration-700 group-hover:scale-110 ${isPopular ? "opacity-10 text-amber-300" : "opacity-10 text-mainBrandColor"}`}>
                <PawPrint size={75} />
            </div>

            <div className="p-5 relative z-10 flex flex-col justify-between h-full w-full flex-1">
                <div>
                    <p className={`text-xs uppercase tracking-widest font-semibold ${titleStyles}`}>
                            {packageSubtitle}
          
                    </p>

                    {packageSubtitle && (
                        <h3 className={`text-xl sm:text-2xl font-black mt-1.5 tracking-tight ${subtitleStyles}`}>
                                      {packageTitle}
                        </h3>
                    )}

                    <p className={`mt-2.5 text-xs sm:text-sm leading-relaxed ${descStyles}`}>
                        {description}
                    </p>
                </div>

                <hr className={`border-t ${!hideButton ? "my-4" : "my-2"} ${isPopular ? "border-white/20" : "border-gray-300"}`} />

                {includes && (
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                        {includes && includes.length > 0 && (
                            includes.map((item, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-1.5 group/item backdrop-blur-xs py-1 px-2 rounded-lg border transition-all duration-300 ${featureChipStyles}`}
                                >
                                    <div
                                        className={`
                                        w-1.5
                                        h-1.5
                                        rounded-full
                                        shrink-0
                                        transition-transform
                                        group-hover/item:scale-125
                                        ${bulletStyles}
                                    `}
                                    />
                                    <p className={`text-[11px] sm:text-xs font-semibold ${featureTextStyles}`}>
                                        {item}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {!hideButton ? (
                    <div className={`pt-2 mt-auto`}>
                        {pricing && (
                            <div className={`mb-3`}>
                                <div className="flex items-baseline gap-2.5 flex-wrap">
                                    {pricing.salePrice ? (
                                        <>
                                            <div className={`text-2xl sm:text-3xl font-black tracking-tight ${priceStyles}`}>
                                                AED {pricing.salePrice}
                                            </div>
                                            <div className={`text-sm sm:text-base line-through font-medium ${isPopular ? "text-white" : "text-gray-600"}`}>
                                                AED {pricing.price}
                                            </div>
                                        </>
                                    ) : (
                                        <div className={`text-2xl sm:text-3xl font-black tracking-tight ${priceStyles}`}>
                                            AED {pricing.price}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <CommonButton
                            text={isPopular ? "Book Best Value" : "Book Package"}
                            className={`w-full rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 ${buttonVariantClass}`}
                            onClick={() => handleSelectPackage?.(pricing)}
                        />
                    </div>
                ) : (
                    <div className={`mt-auto`}>
                        {pricing && (
                            <div>
                                <div className="flex items-baseline gap-2.5 flex-wrap">
                                    {pricing.salePrice ? (
                                        <>
                                            <div className={`text-2xl sm:text-3xl font-black tracking-tight ${priceStyles}`}>
                                                AED {pricing.salePrice}
                                            </div>
                                            <div className={`text-sm sm:text-base line-through font-medium ${isPopular ? "text-white" : "text-gray-600"}`}>
                                                AED {pricing.price}
                                            </div>
                                        </>
                                    ) : (
                                        <div className={`text-2xl sm:text-3xl font-black tracking-tight ${priceStyles}`}>
                                            AED {pricing.price} 
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}


            </div>
        </div>
    );
};

export default PackageCard;